<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Tooltip from 'primevue/tooltip';
import Card from 'primevue/card';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseMasonry from '@/components/base/BaseMasonry.vue';
import EntityLink from '@/components/base/EntityLink.vue';
import JsonViewer from '@/components/base/JsonViewer.vue';
import KycPepTitularV1 from '@/components/company-kyc/data-renderers/KycPepTitularV1.vue';
import KycPepRelatedV1 from '@/components/company-kyc/data-renderers/KycPepRelatedV1.vue';
import KycEconomicActivityV1 from '@/components/company-kyc/data-renderers/KycEconomicActivityV1.vue';
import KycLocationCountryV1 from '@/components/company-kyc/data-renderers/KycLocationCountryV1.vue';
import KycLocationCityV1 from '@/components/company-kyc/data-renderers/KycLocationCityV1.vue';
import KycAgeCompanyV1 from '@/components/company-kyc/data-renderers/KycAgeCompanyV1.vue';
import KycNegativeMediaV1 from '@/components/company-kyc/data-renderers/KycNegativeMediaV1.vue';
import KycLawsuitsV1 from '@/components/company-kyc/data-renderers/KycLawsuitsV1.vue';
import KycLawsuitsNovoV1 from '@/components/company-kyc/data-renderers/KycLawsuitsNovoV1.vue';
import KycNationalSanctionsV1 from '@/components/company-kyc/data-renderers/KycNationalSanctionsV1.vue';
import { formatTimeAgo } from '@/utils/time';
import {
  getRelationshipIcon,
  getRelationshipLabel,
  getCategoryIcon,
  getRiskCategoryLabel,
  getRiskLevelDisplay,
  getHighestRiskLevelValue
} from '@/utils/kyc';
import type { KycCategory, KycEntity } from '@/types/kyc';
import { formatDate, formatDateTime } from '@/utils/formatters';

// --- Interfaces ---

interface VisibleCheck {
  key: string;
  label: string;
  icon: string;
  getSortValue: (entity: KycEntity) => number;
}

interface Props {
  entities: KycEntity[];
  resolvedProfile: any; // Looser type to accept composable's structure
  loading?: boolean;
  categoryOrder?: string[];
  reportGeneratedAt?: string | null;
}

// --- Component Setup ---

defineOptions({
  directives: { tooltip: Tooltip }
});

const props = withDefaults(defineProps<Props>(), {
  entities: () => [],
  resolvedProfile: () => ({}), // Keep resolvedProfile for now, but internal logic is removed
  loading: false,
  categoryOrder: () => [],
  reportGeneratedAt: null,
});

const expandedRows: Ref<any[]> = ref([]);

const visibleCategories: ComputedRef<VisibleCheck[]> = computed(() => {
  const categoryKeys = new Set<string>();
  for (const entity of props.entities) {
    if (entity.categories && Array.isArray(entity.categories)) {
      for (const category of entity.categories) {
        categoryKeys.add(category.category_slug);
      }
    }
  }

  const statusOrder: Record<string, number> = { 'failed': 1, 'missing': 2, 'passed': 3 };

  return Array.from(categoryKeys)
    .map(key => ({
      key,
      label: getRiskCategoryLabel(key),
      icon: getCategoryIcon(key),
      getSortValue: (entity: KycEntity): number => {
        const catData = entity.categories?.find(c => c.category_slug === key);
        return catData?.is_relevant === false ? 4 : (statusOrder[catData?.check_status || 'missing'] || 5);
      },
    }))
    .sort((a, b) => {
      const indexA = props.categoryOrder.indexOf(a.key);
      const indexB = props.categoryOrder.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return a.label.localeCompare(b.label);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
});

const visibleCategoriesMap = computed(() => {
  const map = new Map<string, VisibleCheck>();
  for (const vc of visibleCategories.value) {
    map.set(vc.key, vc);
  }
  return map;
});

const sortedEntities: ComputedRef<KycEntity[]> = computed(() => {
  const processed = props.entities.map(entity => {
    const categorySortValues: Record<string, number> = {};
    visibleCategories.value.forEach(category => {
      categorySortValues[category.key] = category.getSortValue(entity);
    });
    const newEntity: KycEntity = { ...entity, categorySortValues };
    // Prefer backend risk level, fallback to category max.
    newEntity.highestRiskLevelValue = entity.risk_level_value ?? getHighestRiskLevelValue(entity.categories || []);
    // Calculate if any category is outdated
    newEntity.entityIsOutdated = (entity.categories || []).some(c => c.is_relevant !== false && c.is_outdated);
    return newEntity;
  });

  return processed;
});

const getCategoryForEntity = (entity: KycEntity, categoryKey: string): KycCategory | undefined =>
  entity.categories?.find(c => c.category_slug === categoryKey);

const isCategoryMissing = (category?: KycCategory): boolean =>
  !category || category.is_missing || category.check_status === 'missing' || category.level_value === null || category.level_value === undefined;

const getCategoryDisplay = (category?: KycCategory) =>
  getRiskLevelDisplay(
    category?.level_value,
    isCategoryMissing(category) ? { isMissing: true } : undefined
  );

const getCategoryTooltip = (category?: KycCategory): string =>
  (isCategoryMissing(category) ? 'Dados ausentes' : getRiskLevelDisplay(category?.level_value).label);

/** Categories whose detail tables are too wide for a single column. */
const WIDE_CATEGORY_SLUGS = ['pep_titular', 'lawsuits', 'lawsuits_novo'];

const isFullWidthCategory = (category: KycCategory): boolean =>
  WIDE_CATEGORY_SLUGS.includes(category.category_slug) && !!category.data_json;

const getExpansionCategories = (entity: KycEntity): KycCategory[] => {
  const relevant = entity.categories?.filter(c => visibleCategoriesMap.value.has(c.category_slug) && c.is_relevant) ?? [];
  return [
    ...relevant.filter(c => !isFullWidthCategory(c)),
    ...relevant.filter(isFullWidthCategory),
  ];
};

/** Tag props from `RISK_LEVEL_CONFIG` (same pattern as `KycEconomicActivityV1`). */
const getCategoryRiskTagAttrs = (category: KycCategory) => {
  const d = getCategoryDisplay(category);
  return {
    value: d.label,
    style: {
      backgroundColor: d.hexBgColor,
      color: d.hexColor,
    },
    class: [d.borderClass, 'border'],
  };
};

</script>

<template>
  <div class="w-full min-w-0 max-w-full [contain:inline-size]">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
      <div class="flex items-center gap-2">
        <h2 class="text-xl font-semibold text-gray-900">Análise de Compliance por Entidade</h2>
      </div>
      <div v-if="props.reportGeneratedAt" class="text-sm text-gray-500">
        Relatório gerado em: {{ formatDateTime(props.reportGeneratedAt) }}
        <span class="ml-2 text-gray-400">{{ formatTimeAgo(props.reportGeneratedAt) }}</span>
      </div>
    </div>

    <DataTable :value="sortedEntities" dataKey="entity_tax_id" v-model:expandedRows="expandedRows" :loading="loading"
      :rowHover="true" size="small" class="w-full max-w-full text-xs" removableSort>
      <Column expander style="width: 3rem" bodyClass="!py-3" />

      <Column header="Risco" sortable sortField="highestRiskLevelValue" bodyClass="!py-3">
        <template #body="{ data }: { data: KycEntity }">
          <div class="flex items-center justify-center">
            <!-- Risk-based status icon with outdated indicator -->
            <div
              v-tooltip.bottom="getRiskLevelDisplay(data.highestRiskLevelValue ?? 0).label"
              class="relative flex items-center justify-center"
            >
              <BaseIcon :class="['text-2xl', getRiskLevelDisplay(data.highestRiskLevelValue ?? 0).colorClass]">
                {{ getRiskLevelDisplay(data.highestRiskLevelValue ?? 0).icon }}
              </BaseIcon>
              <div
                v-if="data.entityIsOutdated"
                class="absolute -top-1 -right-1 bg-white rounded-full flex items-center justify-center"
              >
                <BaseIcon class="text-sm text-orange-500">schedule</BaseIcon>
              </div>
            </div>
          </div>
        </template>
      </Column>

      <Column header="Entidades" sortable sortField="name" bodyClass="!py-3">
        <template #body="{ data }: { data: KycEntity }">
          <div class="flex min-w-0 max-w-64 items-center gap-2"
            :class="data.is_main_company ? 'bg-blue-50 rounded px-2 py-1' : ''">
            <EntityLink :tax-id="data.entity_tax_id" :type="data.entity_type" :name="data.name" section="kyc" size="compact" />
            <!-- <div class="flex flex-col" v-else>
              <span class="text-sm font-medium text-gray-900">{{ data.entity_tax_id }}</span>
              <span class="text-xs text-gray-500">{{ data.name }}</span>
            </div> -->
          </div>
        </template>
      </Column>

      <Column sortable sortField="relationship" style="width: 5rem" bodyClass="!py-3">
        <template #header>
          <span v-tooltip.top="'Relacionamento'" aria-label="Relacionamento">Relac.</span>
        </template>
        <template #body="{ data }: { data: KycEntity }">
          <span
            v-tooltip.top="getRelationshipLabel(data.relationship)"
            :aria-label="getRelationshipLabel(data.relationship)"
            :title="getRelationshipLabel(data.relationship)"
            tabindex="0"
            class="inline-flex h-7 w-7 items-center justify-center rounded-full"
            :class="data.is_main_company ? 'bg-blue-50 text-blue-600' : 'bg-surface-100 text-surface-600'"
          >
            <BaseIcon class="text-base">{{ getRelationshipIcon(data.relationship) }}</BaseIcon>
          </span>
        </template>
      </Column>

      <Column v-for="col in visibleCategories" :key="col.key" :sortField="'categorySortValues.' + col.key" sortable bodyClass="!py-3">
        <template #header>
          <span v-tooltip.bottom="col.label" class="inline-flex items-center justify-center w-full">
            <BaseIcon class="text-gray-700 text-lg">{{ col.icon }}</BaseIcon>
          </span>
        </template>
        <template #body="{ data }: { data: KycEntity }">
          <div class="flex items-center justify-center">
            <template v-if="getCategoryForEntity(data, col.key)?.is_relevant === false">
              <span class="text-xs text-slate-400 font-medium">N/A</span>
            </template>
            <template v-else>
              <div class="relative">
                <span
                  v-tooltip.bottom="getCategoryTooltip(getCategoryForEntity(data, col.key))"
                  :class="[
                    'inline-flex items-center justify-center w-7 h-7 rounded-full border',
                    getCategoryDisplay(getCategoryForEntity(data, col.key)).bgClass,
                    getCategoryDisplay(getCategoryForEntity(data, col.key)).colorClass,
                    getCategoryDisplay(getCategoryForEntity(data, col.key)).borderClass
                  ]"
                >
                  <BaseIcon class="text-base">{{ getCategoryDisplay(getCategoryForEntity(data, col.key)).icon }}</BaseIcon>
                </span>
                <!-- Outdated indicator -->
                <div
                  v-if="getCategoryForEntity(data, col.key)?.is_outdated"
                  v-tooltip.top="'Última checagem: ' + (getCategoryForEntity(data, col.key)?.last_checked_at ? formatDate(getCategoryForEntity(data, col.key)?.last_checked_at!, undefined, '') + ' (' + formatTimeAgo(getCategoryForEntity(data, col.key)?.last_checked_at!) + ')' : 'Unknown')"
                  class="absolute -top-1 -right-1 bg-white rounded-full flex items-center justify-center"
                >
                  <BaseIcon class="text-xs text-orange-500">schedule</BaseIcon>
                </div>
              </div>
            </template>
          </div>
        </template>
      </Column>

      <template #expansion="{ data }: { data: KycEntity }">
        <div class="w-0 min-w-full px-3 pt-3 bg-gray-50 rounded-md">
          <BaseMasonry
            :items="getExpansionCategories(data)"
            :is-full-width="isFullWidthCategory"
            :gap="12"
          >
            <template #default="{ item: category }">
              <Card class="min-w-0 border">
                <template #content>
                  <div class="flex items-start gap-3">
                    <BaseIcon class="text-[20px] text-gray-800">{{ visibleCategoriesMap.get(category.category_slug)?.icon }}</BaseIcon>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center ">
                        <h4 class="font-medium text-gray-900 text-sm">{{ visibleCategoriesMap.get(category.category_slug)?.label }}</h4>
                        <div class="ml-2">
                          <template v-if="category.is_relevant === false">
                            <Tag value="N/A" severity="secondary" />
                          </template>
                          <template v-else>
                            <Tag v-bind="getCategoryRiskTagAttrs(category)" />
                          </template>
                        </div>

                      </div>
                      <div class="text-xs text-gray-600 mt-1">
                        <template v-if="category.is_relevant === false">
                          <div class="text-gray-500">Not applicable to this entity</div>
                        </template>
                        <template v-else>
                          <div>Última checagem: {{
                            category.last_checked_at ? formatDateTime(category.last_checked_at) : '—'
                          }}
                          </div>
                          <div v-if="category.notes" class="mt-1 text-gray-500">Notes: {{
                            category.notes
                          }}
                          </div>
                        </template>
                      </div>
                      <template v-if="category.data_json">
                        <template v-if="category.category_slug === 'pep_titular' && (category.data_version === '1' || category.data_version === '1.01')">
                          <KycPepTitularV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'pep_related' && (category.data_version === '1' || category.data_version === '1.01')">
                          <KycPepRelatedV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'economic_activity' && category.data_version === '1'">
                          <KycEconomicActivityV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'age_company' && category.data_version === '1'">
                          <KycAgeCompanyV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'location_city' && category.data_version === '1'">
                          <KycLocationCityV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'location_country' && category.data_version === '1'">
                          <KycLocationCountryV1 :category="category" />
                        </template>
                        <template v-else-if="['national_sanctions', 'sanctions_national'].includes(category.category_slug) && category.data_version === '1'">
                          <KycNationalSanctionsV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'negative_media' && category.data_version === '1'">
                          <KycNegativeMediaV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'lawsuits'">
                          <KycLawsuitsV1 :category="category" />
                        </template>
                        <template v-else-if="category.category_slug === 'lawsuits_novo'">
                          <KycLawsuitsNovoV1 :category="category" />
                        </template>
                        <div v-else class="max-h-[min(30vh,36rem)] overflow-y-auto  overscroll-y-contain">
                          <JsonViewer :json-data="category.data_json" />
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </Card>
            </template>
          </BaseMasonry>
        </div>
      </template>
    </DataTable>

<!--    <KycStatusIcon :status="{ overall: 'passed', is_outdated: true }" />-->
<!--    <KycStatusIcon :status="{ overall: 'missing', is_outdated: true }" />-->
<!--    <KycStatusIcon :status="{ overall: 'missing', is_outdated: true }" />-->
  </div>
</template>
